import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, requestPermission } from './firebase';

import Login from './Login';
import Dashboard from './Dashboard';
import NotificationSettings from './routes/NotificationSettings';

// ── Nav style tokens ──────────────────────────────────────────────────────────
const nav = {
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '10px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    background: 'rgba(15,17,23,0.95)',
    backdropFilter: 'blur(12px)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 800,
    fontSize: 15,
    color: '#e8eaf0',
    letterSpacing: '-0.3px',
    textDecoration: 'none',
    marginRight: 12,
  },
  brandIcon: {
    width: 28,
    height: 28,
    borderRadius: 7,
    background: 'linear-gradient(135deg, #6366f1, #818cf8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    boxShadow: '0 0 0 1px rgba(99,102,241,0.35)',
  },
  sep: {
    width: 1,
    height: 16,
    background: 'rgba(255,255,255,0.09)',
    margin: '0 8px',
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px',
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 500,
    color: '#6b7280',
    textDecoration: 'none',
    border: '1px solid transparent',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  linkActive: {
    color: '#a5b4fc',
    background: 'rgba(99,102,241,0.13)',
    border: '1px solid rgba(99,102,241,0.25)',
  },
  // applied via onMouseEnter/Leave — minimal hover feel
  linkHover: {
    color: '#e8eaf0',
    background: 'rgba(255,255,255,0.05)',
  },
};

// ── Reusable nav link with hover state ────────────────────────────────────────
function AppNavLink({ to, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        ...nav.link,
        ...(isActive ? nav.linkActive : {}),
        ...(hovered && !isActive ? nav.linkHover : {}),
      })}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </NavLink>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      console.log('AUTH STATE CHANGED:', u);
      setUser(u || null);
      if (u) requestPermission(u.uid);
    });
    return () => unsub();
  }, []);

  if (user === undefined) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      {/* ── Navigation bar ─────────────────────────────────────────── */}
      <nav style={nav.bar}>
        {/* Brand */}
        <NavLink to="/" style={nav.brand}>
          <div style={nav.brandIcon}>📚</div>
          DueQueue
        </NavLink>

        <div style={nav.sep} />

        {!user ? (
          <>
            <AppNavLink to="/">Home</AppNavLink>
            <AppNavLink to="/login">Log in</AppNavLink>
          </>
        ) : (
          <>
            <AppNavLink to="/dashboard">📊 Dashboard</AppNavLink>
            <AppNavLink to="/settings/notifications">⚙️ Notifications</AppNavLink>
          </>
        )}
      </nav>

      {/* ── Routes ─────────────────────────────────────────────────── */}
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" replace />} />
        <Route
          path="/settings/notifications"
          element={user ? <NotificationSettings /> : <Navigate to="/login" replace />}
        />
      </Routes>
    </Router>
  );
}

export default App;